/**
 * Internal-machine analytics opt-out for the PostHog capture floor.
 *
 * WHY: the project-level "internal and test users" filter excludes the team's
 * IDENTIFIED activity (person email contains "krempovych" / "@bookeper.pl" /
 * "bookeper.test"), but anonymous browsing from team machines — logged-out
 * sessions, secondary browsers, quick prod checks — is indistinguishable from
 * a real visitor server-side. This module gives every team machine a
 * per-browser kill switch enforced client-side in `before_send`, the same
 * layer as the bot floor (sibling of bot-detection.ts).
 *
 * FOOLPROOFING (v2, 2026-07-22):
 *   - The flag lives in a `.bookeper.pl` DOMAIN COOKIE (400 days, the Chrome
 *     max), so ONE mark covers app + www + accounting at once. localStorage
 *     is kept as a same-host mirror for cookie-blocked browsers.
 *   - `markIfInternalEmail()` is called from the frontends' identify path:
 *     any browser that ever signs in with a team account gets marked
 *     automatically — zero manual steps for the team.
 *   - The `?bkpr_internal=1|0` URL param remains the manual path for
 *     never-logged-in browsers (and the only way to UNmark).
 *
 * Automation (Playwright/crabbox/headless) is already dropped by
 * `isLikelyBot()` (navigator.webdriver); this covers the human side.
 *
 * SSR-safe and fail-open: no window/cookie/storage (or a storage that throws,
 * e.g. Safari private-mode quota) → treated as NOT internal, never an error.
 */

const STORAGE_KEY = 'bkpr_internal_machine';
const COOKIE_NAME = 'bkpr_internal';
const URL_PARAM = 'bkpr_internal';
// Short alias (?bk_internal=1|0), same semantics.
const URL_PARAM_ALIAS = 'bk_internal';
// 400 days — Chrome's Max-Age ceiling; refreshed on every re-mark/login.
const COOKIE_MAX_AGE_SECONDS = 400 * 24 * 60 * 60;

/**
 * Team-account discriminator, mirroring the PostHog project's
 * "internal and test users" filter (email not_icontains
 * krempovych / @bookeper.pl / bookeper.test). Keep the two in sync.
 */
export const INTERNAL_EMAIL_PATTERN =
  /krempovych|vlad\.lisnyi|vladlesnoy|@bookeper\.pl$|bookeper\.test$/i;

/**
 * QA / e2e persona addresses: `qa-test-*@bookeper.test` (test_user.py mint),
 * `e2e-*@bookeper.local` (Playwright signup specs) and the gate-run integration
 * fixtures `(e2e|test)-<8 hex>@example.com`. Their traffic is tagged `is_internal`
 * (event + person property) but, unlike a team sign-in, never marks the browser.
 */
export const QA_PERSONA_EMAIL_PATTERN =
  /^(?:qa-test-.+@bookeper\.test|e2e-.+@bookeper\.local|(?:e2e|test)-[0-9a-f]{8}@example\.com)$/i;

export function isQaPersonaEmail(email: string | null | undefined): boolean {
  return typeof email === 'string' && QA_PERSONA_EMAIL_PATTERN.test(email.trim());
}

interface CookieJarLike {
  get(): string;
  set(value: string): void;
}

interface InternalMachineEnvLike {
  search?: string;
  hostname?: string;
  storage?: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;
  cookies?: CookieJarLike;
}

function resolveEnv(): InternalMachineEnvLike {
  const g = globalThis as typeof globalThis & {
    location?: { search?: string; hostname?: string };
    localStorage?: Storage;
    document?: { cookie: string };
  };
  const doc = g.document;
  return {
    search: g.location?.search,
    hostname: g.location?.hostname,
    storage: g.localStorage,
    cookies: doc
      ? {
          get: () => doc.cookie,
          set: (value: string) => {
            doc.cookie = value;
          },
        }
      : undefined,
  };
}

function cookieDomainAttr(hostname: string | undefined): string {
  // Domain cookie only on our own apex — a host-only cookie everywhere else
  // (localhost, previews) so we never emit an invalid Domain attribute.
  if (hostname && (hostname === 'bookeper.pl' || hostname.endsWith('.bookeper.pl'))) {
    return '; domain=.bookeper.pl';
  }
  return '';
}

function readCookieFlag(env: InternalMachineEnvLike): boolean {
  const raw = env.cookies?.get() ?? '';
  return raw.split(';').some((part) => part.trim() === `${COOKIE_NAME}=1`);
}

function writeFlag(env: InternalMachineEnvLike, on: boolean): void {
  const domain = cookieDomainAttr(env.hostname);
  if (env.cookies) {
    const maxAge = on ? COOKIE_MAX_AGE_SECONDS : 0;
    env.cookies.set(
      `${COOKIE_NAME}=${on ? '1' : ''}; path=/${domain}; max-age=${maxAge}; SameSite=Lax`
    );
  }
  if (env.storage) {
    if (on) {
      env.storage.setItem(STORAGE_KEY, '1');
    } else {
      env.storage.removeItem(STORAGE_KEY);
    }
  }
}

/**
 * True when this browser has been marked as an internal team machine.
 *
 * Reads the `?bkpr_internal=1|0` URL param first (persisting any change to
 * both the domain cookie and localStorage — so the marking pageview itself is
 * already suppressed), then falls back to cookie OR stored flag.
 *
 * @param envOverride injectable location/cookie/storage for tests
 */
export function isInternalMachine(envOverride?: InternalMachineEnvLike): boolean {
  const env = envOverride ?? resolveEnv();
  try {
    const params = new URLSearchParams(env.search ?? '');
    const param = params.get(URL_PARAM) ?? params.get(URL_PARAM_ALIAS);
    if (param === '1') {
      writeFlag(env, true);
      return true;
    }
    if (param === '0') {
      writeFlag(env, false);
      return false;
    }
    return readCookieFlag(env) || env.storage?.getItem(STORAGE_KEY) === '1';
  } catch {
    // Storage/cookie quota or security errors (private mode, blocked cookies)
    // — a real visitor's analytics must never be dropped by our own guard.
    return false;
  }
}

/**
 * Mark this browser as an internal machine (idempotent; refreshes the 400-day
 * cookie). Called from the identify path when a team account signs in, so the
 * mark requires no manual step and outlives the login session.
 */
export function markInternalMachine(envOverride?: InternalMachineEnvLike): void {
  const env = envOverride ?? resolveEnv();
  try {
    writeFlag(env, true);
  } catch {
    // fail-open
  }
}

/**
 * Auto-mark hook for the frontends' identify path: marks the browser when the
 * signing-in account matches the internal-team pattern. Returns whether the
 * browser is (now) marked.
 */
export function markIfInternalEmail(
  email: string | null | undefined,
  envOverride?: InternalMachineEnvLike
): boolean {
  if (!email || !INTERNAL_EMAIL_PATTERN.test(email)) {
    return false;
  }
  markInternalMachine(envOverride);
  return true;
}
